import usePageEditorStore from "@/components/PageEditor/store/usePageEditorStore";
import { useEditor } from "@craftjs/core";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import RedoIcon from "@mui/icons-material/Redo";
import UndoIcon from "@mui/icons-material/Undo";
import { Divider, ToggleButton } from "@mui/material";
import { createElement, useEffect } from "react";
import StyledToggleButtonGroup from "./components/StyledToggleButtonGroup/StyledToggleButtonGroup";

const Toolbar = () => {
    const { active, enabled, related, canUndo, canRedo, actions } = useEditor((state, query) => {
        const currentlySelectedNodeId = query.getEvent("selected").first();

        return {
            active: currentlySelectedNodeId,
            related: currentlySelectedNodeId && state.nodes[currentlySelectedNodeId].related,
            enabled: state.options.enabled,
            canUndo: query.history.canUndo(),
            canRedo: query.history.canRedo(),
        };
    });

    const fullscreen = usePageEditorStore((state) => state.fullscreen);
    const setFullscreen = usePageEditorStore((state) => state.setFullscreen);

    useEffect(() => {
        const handleFullscreenChange = () => {
            setFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener("fullscreenchange", handleFullscreenChange);

        return () => {
            document.removeEventListener("fullscreenchange", handleFullscreenChange);
        };
    }, [setFullscreen]);

    const toggleFullscreen = () => {
        const viewport = document.getElementById("viewport");

        if (!viewport) return;

        if (!document.fullscreenElement) {
            viewport.requestFullscreen();
        } else {
            document.exitFullscreen();
        }
    };

    if (!enabled) return null;

    return (
        <div className="craftjs-toolbar flex w-full items-center p-4 bg-neutral-200">
            <div className="flex flex-1 flex-wrap border border-black/12 bg-white rounded-sm">
                <StyledToggleButtonGroup size="small" aria-label="history actions">
                    <ToggleButton
                        value="undo"
                        aria-label="undo"
                        disabled={!canUndo}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            actions.history.undo();
                        }}
                    >
                        <UndoIcon />
                    </ToggleButton>
                    <ToggleButton
                        value="redo"
                        aria-label="redo"
                        disabled={!canRedo}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            actions.history.redo();
                        }}
                    >
                        <RedoIcon />
                    </ToggleButton>
                </StyledToggleButtonGroup>

                {active && related.toolbar && (
                    <>
                        <Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 1 }} />
                        {createElement(related.toolbar)}
                    </>
                )}

                <Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 1 }} />
                <StyledToggleButtonGroup size="small" aria-label="fullscreen actions">
                    <ToggleButton
                        value="fullscreen"
                        aria-label="fullscreen"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            toggleFullscreen();
                        }}
                    >
                        {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                    </ToggleButton>
                </StyledToggleButtonGroup>
            </div>
        </div>
    );
};

export default Toolbar;
